/**
 * A turn's tool use, compact. Collapsed (the default) it is one line: how many
 * tools ran, how many failed, and the latest action — which changes as the
 * turn works, so the user can follow along without a wall of text. Expanded
 * it lists one short line per call (the call and its result merged), and each
 * line opens to the raw input and output on demand.
 *
 * Shown only when the user turns "Tool activity" on (see
 * `useForgeShowToolActivity`); off by default.
 */
import { useMemo, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Wrench,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ForgeToolEvent } from '../types';

export interface ToolRow {
  key: string;
  name: string;
  summary: string | null;
  status: 'running' | 'ok' | 'error';
  input: unknown;
  result?: unknown;
}

const WORKSPACE_PREFIX = /^\/workspace\//;

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** "mcp__playwright__browser_take_screenshot" → "Browser: take screenshot". */
export function toolDisplayName(name: string): string {
  const mcp = /^mcp__([a-z0-9-]+)__(.+)$/i.exec(name);
  if (mcp) {
    const action = mcp[2].replace(/^browser_/, '').replace(/_/g, ' ');
    return mcp[1] === 'playwright' ? `Browser: ${action}` : `${mcp[1]}: ${action}`;
  }
  return name;
}

/** The one-line gist of a Claude Code tool call. */
export function toolSummary(name: string, input: unknown): string | null {
  const i = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const path = str(i.file_path) ?? str(i.path) ?? str(i.notebook_path);
  if (path) return path.replace(WORKSPACE_PREFIX, '');
  const command = str(i.command);
  if (command) return command.split('\n')[0];
  const pattern = str(i.pattern);
  if (pattern) return pattern;
  const url = str(i.url);
  if (url) return url;
  if (name === 'TodoWrite' && Array.isArray(i.todos)) return `${i.todos.length} to-dos`;
  const described = str(i.description) ?? str(i.element) ?? str(i.text);
  if (described) return described;
  return null;
}

/** Merge each tool_call with its tool_result (by toolUseId, else the latest open call of that tool). */
export function pairToolEvents(events: ForgeToolEvent[]): ToolRow[] {
  const rows: ToolRow[] = [];
  const byId = new Map<string, ToolRow>();
  events.forEach((event, index) => {
    const name = event.data.toolName ?? 'tool';
    if (event.type === 'tool_call') {
      const row: ToolRow = {
        key: event.data.toolUseId ?? `call-${index}`,
        name,
        summary: toolSummary(name, event.data.toolInput),
        status: 'running',
        input: event.data.toolInput,
      };
      rows.push(row);
      if (event.data.toolUseId) byId.set(event.data.toolUseId, row);
      return;
    }
    const target =
      (event.data.toolUseId && byId.get(event.data.toolUseId)) ||
      [...rows].reverse().find((r) => r.status === 'running' && r.name === name);
    if (target) {
      target.status = event.data.isError ? 'error' : 'ok';
      target.result = event.data.toolResult;
    }
  });
  return rows;
}

function detail(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function StatusIcon({ status }: { status: ToolRow['status'] }) {
  if (status === 'running') return <Loader2 className="h-3 w-3 animate-spin shrink-0" />;
  if (status === 'error') return <AlertCircle className="h-3 w-3 text-destructive shrink-0" />;
  return <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />;
}

function Row({ row }: { row: ToolRow }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-2 py-0.5 text-left hover:bg-muted/60"
        aria-expanded={open}
      >
        <StatusIcon status={row.status} />
        <span className="shrink-0 font-medium">{toolDisplayName(row.name)}</span>
        {row.summary && (
          <span className="truncate font-mono text-muted-foreground">{row.summary}</span>
        )}
      </button>
      {open && (
        <div className="mx-2 mb-1 space-y-1">
          <pre className="max-h-40 overflow-auto rounded bg-muted p-2 text-[11px] whitespace-pre-wrap break-all">
            {detail(row.input)}
          </pre>
          {row.result !== undefined && (
            <pre
              className={cn(
                'max-h-40 overflow-auto rounded p-2 text-[11px] whitespace-pre-wrap break-all',
                row.status === 'error' ? 'bg-destructive/10' : 'bg-muted',
              )}
            >
              {detail(row.result)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

export interface ForgeToolActivityProps {
  events: ForgeToolEvent[];
  /** The turn is still running: the collapsed line follows the latest action. */
  live?: boolean;
}

export function ForgeToolActivity({ events, live = false }: ForgeToolActivityProps) {
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => pairToolEvents(events), [events]);
  if (rows.length === 0) return null;
  const failed = rows.filter((r) => r.status === 'error').length;
  const latest = rows[rows.length - 1];

  return (
    <div
      className="mt-1 rounded-md border bg-background/60 text-xs"
      data-testid="forge-tool-activity"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-2 px-2 py-1 text-left text-muted-foreground"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" />
        )}
        <Wrench className="h-3.5 w-3.5 shrink-0" />
        <span className="shrink-0">
          {rows.length} tool {rows.length === 1 ? 'call' : 'calls'}
          {failed > 0 && <span className="text-destructive"> · {failed} failed</span>}
        </span>
        {!expanded && (
          <span className="flex min-w-0 items-center gap-1.5" data-testid="forge-tool-latest">
            <span className="shrink-0">·</span>
            {live && latest.status === 'running' && (
              <Loader2 className="h-3 w-3 animate-spin shrink-0" />
            )}
            <span className="truncate">
              {toolDisplayName(latest.name)}
              {latest.summary ? ` ${latest.summary}` : ''}
            </span>
          </span>
        )}
      </button>
      {expanded && (
        <ul className="max-h-64 overflow-y-auto border-t py-1">
          {rows.map((row) => (
            <Row key={row.key} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}
